import { useCallback, useMemo, useEffect, useState } from 'react'
import { useHistory, generatePath } from 'react-router-dom'

import ROUTES from 'app/routes'
import NetworkWizardLayout from '../../NetworkWizardLayout'
import {
  CreateWLANsContainer,
  CreateWLANsHeaderWrapper,
  StyledNetworkWizardBottomPanel,
} from '../styles'
import {
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../../styles'
import {
  WLANListRow,
  WLANListItemBody,
  WLANListLeftBlock,
  WLANListRightBlock,
  WLANListHeaderTitle,
} from './styles'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useNetworkHomeJobsRouteParams,
  useCurrentHomeSiteId,
  useNetworkOrgId,
} from 'features/networkAi/hooks'
import {
  fetchOrgSiteWlansThunk,
  createWLANThunk,
} from 'features/networkAi/slice'
import { orgSiteWlansFetchDataSelector } from 'features/networkAi/selectors'
import { OrgSiteWlans, CreateWLANBody } from 'features/networkAi/types'

import CreateWLANHomeItem from './CreateWLANHomeItem'
import { WhiteBlock } from 'components/Layout'
import { useIntl } from 'react-intl'
import { DEFAULT_WLAN_VALUES } from 'features/networkAi/consts'

function CreateWLANCopyHome() {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const orgId = useNetworkOrgId()
  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()
  const siteId = useCurrentHomeSiteId({ homeId })
  const orgSiteWlans = useAppSelector(orgSiteWlansFetchDataSelector.data)
  const isLoading = useAppSelector(orgSiteWlansFetchDataSelector.isFetching)

  const orgSiteWlansWithNew: OrgSiteWlans[] = Array.isArray(orgSiteWlans)
    ? orgSiteWlans
    : [{} as OrgSiteWlans]

  const [templateItems, setTemplateItems] = useState<Array<string>>([])
  const [anyTemplatesSelected, setAnyTemplatesSelected] = useState(true)

  const handleBackButtonClick = useCallback(() => {
    const path = generatePath(ROUTES.networkAi.wizard.createWLANs, {
      rootDomainId,
      jobId,
      homeId,
    })

    return history.push(path)
  }, [history, rootDomainId, jobId, homeId])

  const handleNextDoneClick = useCallback(async () => {
    if (templateItems.length > 0) {
      templateItems.forEach(async function (template) {
        if (orgSiteWlans != null) {
          orgSiteWlans.forEach(async function (orgTemplate) {
            if (orgTemplate.id === template) {
              orgTemplate.wlans.forEach(async function (wlan) {
                const newWLANBody = mapTemplateRequest(wlan)
                await dispatch(createWLANThunk(siteId, newWLANBody))
              })
            }
          })
        }
      })

      const path = generatePath(ROUTES.networkAi.wizard.createWLANsSetup, {
        rootDomainId,
        jobId,
        homeId,
      })

      return history.push(path)
    } else {
      const path = generatePath(ROUTES.networkAi.wizard.createWLANsSetup, {
        rootDomainId,
        jobId,
        homeId,
      })

      return history.push(path)
    }
  }, [
    history,
    rootDomainId,
    jobId,
    homeId,
    templateItems,
    orgSiteWlans,
    dispatch,
    siteId,
  ])

  const mapTemplateRequest = (values: CreateWLANBody): CreateWLANBody => {
    return Object.keys(values).reduce((acc, key) => {
      let newValues: CreateWLANBody = { ...acc }
      switch (key) {
        case 'id':
          newValues.id = ''
          break
        default:
          newValues = {
            ...newValues,
            [key]: values[key as keyof CreateWLANBody],
          }
      }
      return newValues
    }, DEFAULT_WLAN_VALUES as CreateWLANBody)
  }

  const bottomBlock = useMemo(() => {
    return (
      <StyledNetworkWizardBottomPanel>
        <NetworkWizardCancelButton
          variant="secondary"
          type="button"
          onClick={handleBackButtonClick}
        >
          {formatMessage(commonMessages.back)}
        </NetworkWizardCancelButton>
        <NetworkWizardSubmitButton
          variant="primary"
          type="submit"
          disabled={anyTemplatesSelected}
          onClick={handleNextDoneClick}
        >
          {formatMessage(commonMessages.next)}
        </NetworkWizardSubmitButton>
      </StyledNetworkWizardBottomPanel>
    )
  }, [
    handleBackButtonClick,
    handleNextDoneClick,
    formatMessage,
    anyTemplatesSelected,
  ])

  function removeItem<T>(arr: Array<T>, value: T): Array<T> {
    const index = arr.indexOf(value)
    if (index > -1) {
      arr.splice(index, 1)
    }
    return arr
  }

  const handleTemplateSelected = (isSelected: boolean, id: string) => {
    if (isSelected) {
      templateItems.push(id)
    } else {
      removeItem(templateItems, id)
    }
    if (templateItems.length === 0) {
      setAnyTemplatesSelected(true)
    } else {
      setAnyTemplatesSelected(false)
    }
  }

  useEffect(() => {
    orgId && dispatch(fetchOrgSiteWlansThunk(orgId))
    setTemplateItems([])
  }, [dispatch, orgId])

  return (
    <NetworkWizardLayout bottomBlock={bottomBlock} currentWizardStepId={4}>
      <CreateWLANsContainer>
        <CreateWLANsHeaderWrapper>
          <NetworkWizardTitle>
            Apply Existing Home As Template
          </NetworkWizardTitle>
        </CreateWLANsHeaderWrapper>

        <WhiteBlock>
          <WLANListRow>
            <WLANListLeftBlock>
              <WLANListItemBody>
                <WLANListHeaderTitle>Home Name</WLANListHeaderTitle>
              </WLANListItemBody>
            </WLANListLeftBlock>
            <WLANListRightBlock>
              <WLANListItemBody>Number of WLANs</WLANListItemBody>
            </WLANListRightBlock>
          </WLANListRow>

          {isLoading ? (
            <SpinnerCentered mt="200px" />
          ) : (
            orgSiteWlans &&
            orgSiteWlansWithNew.map((orgSiteWlans, i) => {
              return (
                orgSiteWlans.wlans.length !== 0 && (
                  <>
                    <div key={orgSiteWlans.name + i}>
                      <CreateWLANHomeItem
                        key={orgSiteWlans.name + i}
                        orgSiteWlan={orgSiteWlans}
                        onSelectedTemplate={handleTemplateSelected}
                      />
                    </div>
                  </>
                )
              )
            })
          )}
        </WhiteBlock>
      </CreateWLANsContainer>
    </NetworkWizardLayout>
  )
}

export default CreateWLANCopyHome
